import { collection, getDocs, doc, getDoc, addDoc, updateDoc, setDoc, query, where } from 'firebase/firestore';
// Commented out for production - used for Firestore push functionality
// import { deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getCurrentMillis } from '../utils/dateUtils';
import type { LawyerGuideCategory, LawyerGuideItem } from '../types';

// Collection names
const LAWYER_GUIDE_REF = 'LawyerGuide';

// Check if Firebase is properly initialized
const isFirebaseAvailable = () => {
  try {
    return db && typeof db === 'object';
  } catch (error) {
    console.error('Firebase not available:', error);
    return false;
  }
};

// Get all active categories
export const getLawyerGuideCategories = async (): Promise<LawyerGuideCategory[]> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    const lawyerGuideRef = collection(db, LAWYER_GUIDE_REF);
    const q = query(
      lawyerGuideRef,
      where('isActive', '==', true)
    );
    
    const querySnapshot = await getDocs(q);
    const categories: LawyerGuideCategory[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      
      // Extract items from the items array field
      const items: LawyerGuideItem[] = [];
      if (data.items && Array.isArray(data.items)) {
        data.items.forEach((itemData: any, index: number) => {
          items.push({
            id: `${doc.id}-item-${index}`,
            title: itemData.nameEn || itemData.nameAr || '',
            nameEn: itemData.nameEn || '',
            nameAr: itemData.nameAr || '',
            content: itemData.data || itemData.url || '',
            description: itemData.descriptionEn || itemData.descriptionAr || '',
            descriptionEn: itemData.descriptionEn || '',
            descriptionAr: itemData.descriptionAr || '',
            type: itemData.type || 'link',
            difficulty: itemData.difficulty || 'beginner',
            order: itemData.order || index,
            data: itemData.data || itemData.url || '',
            action: itemData.action || 'open_url',
            categoryId: doc.id,
            isActive: itemData.isActive !== false,
            createdAt: itemData.createdAt || '',
            updatedAt: itemData.updatedAt || '',
            createdBy: itemData.createdBy || '',
            updatedBy: itemData.updatedBy || ''
          });
        });
      }
      
      categories.push({
        id: doc.id,
        title: data.nameEn || data.nameAr || '',
        nameEn: data.nameEn || '',
        nameAr: data.nameAr || '',
        description: data.descriptionEn || data.descriptionAr || '',
        descriptionEn: data.descriptionEn || '',
        descriptionAr: data.descriptionAr || '',
        icon: data.icon || '',
        iconName: data.iconName || '',
        photoURL: data.photoURL || '',
        color: data.color || '#3B82F6',
        order: data.order || 0,
        sortOrder: data.sortOrder || 0,
        isActive: data.isActive !== false,
        createdAt: data.createdAt || '',
        updatedAt: data.updatedAt || '',
        createdBy: data.createdBy || '',
        updatedBy: data.updatedBy || '',
        items: items
      });
    });
    
    return categories;
  } catch (error) {
    console.error('Error fetching lawyer guide categories:', error);
    throw error;
  }
};

// Get items for a specific category from sub-collection
export const getLawyerGuideItems = async (categoryId: string): Promise<LawyerGuideItem[]> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    console.log(`Fetching items from sub-collection for category: ${categoryId}`);

    // Create the sub-collection reference: LawyerGuide/{categoryId}/Items
    const itemsCollectionRef = collection(db, LAWYER_GUIDE_REF, categoryId, 'Items');
    
    // Query the sub-collection
    const q = query(
      itemsCollectionRef,
      where('isActive', '==', true)
    );
    
    const querySnapshot = await getDocs(q);
    const items: LawyerGuideItem[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      items.push({
        id: doc.id, // Use the actual document ID from sub-collection
        title: data.nameEn || data.nameAr || '',
        nameEn: data.nameEn || '',
        nameAr: data.nameAr || '',
        content: data.data || data.url || '',
        description: data.descriptionEn || data.descriptionAr || '',
        descriptionEn: data.descriptionEn || '',
        descriptionAr: data.descriptionAr || '',
        type: data.type || 'link',
        difficulty: data.difficulty || 'beginner',
        order: data.sortOrder || data.order || 0,
        data: data.data || data.url || '',
        action: data.action || 'open_url',
        categoryId: categoryId,
        isActive: data.isActive !== false,
        createdAt: data.createdAt || '',
        updatedAt: data.updatedAt || '',
        createdBy: data.createdBy || '',
        updatedBy: data.updatedBy || ''
      });
    });
    
    // Sort items by sortOrder
    items.sort((a, b) => a.order - b.order);
    
    console.log(`Fetched ${items.length} items from sub-collection for category: ${categoryId}`);
    return items;
  } catch (error) {
    console.error('Error fetching lawyer guide items from sub-collection:', error);
    throw error;
  }
};

// Get category with its items from sub-collection
export const getLawyerGuideCategoryWithItems = async (categoryId: string): Promise<LawyerGuideCategory | null> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    // Get category
    const categoryRef = doc(db, LAWYER_GUIDE_REF, categoryId);
    const categorySnap = await getDoc(categoryRef);
    
    if (!categorySnap.exists()) {
      return null;
    }
    
    const categoryData = categorySnap.data();
    
    // Get items from sub-collection
    const items = await getLawyerGuideItems(categoryId);
    
    const category: LawyerGuideCategory = {
      id: categorySnap.id,
      title: categoryData?.nameEn || categoryData?.nameAr || '',
      nameEn: categoryData?.nameEn || '',
      nameAr: categoryData?.nameAr || '',
      description: categoryData?.descriptionEn || categoryData?.descriptionAr || '',
      descriptionEn: categoryData?.descriptionEn || '',
      descriptionAr: categoryData?.descriptionAr || '',
      icon: categoryData?.icon || '',
      iconName: categoryData?.iconName || '',
      photoURL: categoryData?.photoURL || '',
      color: categoryData?.color || '#3B82F6',
      order: categoryData?.order || categoryData?.sortOrder || 0,
      sortOrder: categoryData?.sortOrder || 0,
      isActive: categoryData?.isActive !== false,
      createdAt: categoryData?.createdAt || '',
      updatedAt: categoryData?.updatedAt || '',
      createdBy: categoryData?.createdBy || '',
      updatedBy: categoryData?.updatedBy || '',
      items: items
    };
    
    return category;
  } catch (error) {
    console.error('Error fetching category with items:', error);
    throw error;
  }
};

// Search categories and items
export const searchLawyerGuide = async (searchTerm: string): Promise<{
  categories: LawyerGuideCategory[];
  items: LawyerGuideItem[];
}> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    const searchLower = searchTerm.toLowerCase();
    
    // Get all categories
    const categories = await getLawyerGuideCategories();
    
    // Get all items from sub-collections for each category
    const allItems: LawyerGuideItem[] = [];
    for (const category of categories) {
      try {
        const categoryItems = await getLawyerGuideItems(category.id);
        allItems.push(...categoryItems);
      } catch (error) {
        console.warn(`Error fetching items for category ${category.id}:`, error);
        // Continue with other categories even if one fails
      }
    }
    
    // Filter categories
    const filteredCategories = categories.filter(category => 
      (category.nameEn?.toLowerCase().includes(searchLower) || false) ||
      (category.nameAr?.includes(searchTerm) || false) ||
      (category.descriptionEn && category.descriptionEn.toLowerCase().includes(searchLower)) ||
      (category.descriptionAr && category.descriptionAr.includes(searchTerm))
    );
    
    // Filter items
    const filteredItems = allItems.filter(item => 
      (item.nameEn?.toLowerCase().includes(searchLower) || false) ||
      (item.nameAr?.includes(searchTerm) || false) ||
      (item.descriptionEn && item.descriptionEn.toLowerCase().includes(searchLower)) ||
      (item.descriptionAr && item.descriptionAr.includes(searchTerm))
    );
    
    return {
      categories: filteredCategories,
      items: filteredItems
    };
  } catch (error) {
    console.error('Error searching lawyer guide:', error);
    throw error;
  }
};


// Create a new category
export const createLawyerGuideCategory = async (categoryData: Omit<LawyerGuideCategory, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'>): Promise<string> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    const lawyerGuideRef = collection(db, LAWYER_GUIDE_REF);
    const newCategory = {
      ...categoryData,
      createdAt: getCurrentMillis(),
      updatedAt: getCurrentMillis(),
      createdBy: 'system', // TODO: Get from auth context
      updatedBy: 'system'
    };
    
    const docRef = await addDoc(lawyerGuideRef, newCategory);
    return docRef.id;
  } catch (error) {
    console.error('Error creating lawyer guide category:', error);
    throw error;
  }
};

// Create a new item
export const createLawyerGuideItem = async (itemData: Omit<LawyerGuideItem, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'>): Promise<string> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    const lawyerGuideRef = collection(db, LAWYER_GUIDE_REF);
    const newItem = {
      ...itemData,
      createdAt: getCurrentMillis(),
      updatedAt: getCurrentMillis(),
      createdBy: 'system', // TODO: Get from auth context
      updatedBy: 'system'
    };
    
    const docRef = await addDoc(lawyerGuideRef, newItem);
    return docRef.id;
  } catch (error) {
    console.error('Error creating lawyer guide item:', error);
    throw error;
  }
};

// Update category
export const updateLawyerGuideCategory = async (categoryId: string, updates: Partial<LawyerGuideCategory>): Promise<void> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    const categoryRef = doc(db, LAWYER_GUIDE_REF, categoryId);
    await updateDoc(categoryRef, {
      ...updates,
      updatedAt: getCurrentMillis(),
      updatedBy: 'system' // TODO: Get from auth context
    });
  } catch (error) {
    console.error('Error updating lawyer guide category:', error);
    throw error;
  }
};

// Update item
export const updateLawyerGuideItem = async (itemId: string, updates: Partial<LawyerGuideItem>): Promise<void> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    const itemRef = doc(db, LAWYER_GUIDE_REF, itemId);
    await updateDoc(itemRef, {
      ...updates,
      updatedAt: getCurrentMillis(),
      updatedBy: 'system' // TODO: Get from auth context
    });
  } catch (error) {
    console.error('Error updating lawyer guide item:', error);
    throw error;
  }
};

// Delete category (soft delete)
export const deleteLawyerGuideCategory = async (categoryId: string): Promise<void> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    const categoryRef = doc(db, LAWYER_GUIDE_REF, categoryId);
    await updateDoc(categoryRef, {
      isActive: false,
      updatedAt: getCurrentMillis(),
      updatedBy: 'system' // TODO: Get from auth context
    });
  } catch (error) {
    console.error('Error deleting lawyer guide category:', error);
    throw error;
  }
};

// Delete item (soft delete)
export const deleteLawyerGuideItem = async (itemId: string): Promise<void> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    const itemRef = doc(db, LAWYER_GUIDE_REF, itemId);
    await updateDoc(itemRef, {
      isActive: false,
      updatedAt: getCurrentMillis(),
      updatedBy: 'system' // TODO: Get from auth context
    });
  } catch (error) {
    console.error('Error deleting lawyer guide item:', error);
    throw error;
  }
};

// Push category to Firestore collection /LawyerGuide/{cat-id}/
export const pushCategoryToFirestore = async (category: LawyerGuideCategory): Promise<string> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    // Create the document path /LawyerGuide/{cat-id}
    const docPath = `LawyerGuide/${category.id}`;
    const docRef = doc(db, docPath);
    
    // Prepare the category data with all category fields and empty items array
    const categoryData = {
      // Category fields
      nameEn: category.nameEn,
      nameAr: category.nameAr,
      descriptionEn: category.descriptionEn || '',
      descriptionAr: category.descriptionAr || '',
      photoURL: category.photoURL || '',
      iconName: category.iconName || '',
      color: category.color || '#3B82F6',
      isActive: category.isActive,
      sortOrder: category.sortOrder,
      createdAt: category.createdAt || getCurrentMillis(),
      updatedAt: getCurrentMillis(),
      createdBy: category.createdBy || 'system',
      updatedBy: 'system',
      // Empty items array
      items: []
    };
    
    // Set the document with the category data
    await setDoc(docRef, categoryData);
    return category.id; // Return the original category ID since we're using it as the document ID
  } catch (error) {
    console.error('Error pushing category to Firestore:', error);
    throw error;
  }
};

// Push items to selected category's items array in Firestore
export const pushItemsToCategory = async (categoryId: string, items: LawyerGuideItem[]): Promise<void> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    // Create the document path /LawyerGuide/{cat-id}
    const docPath = `LawyerGuide/${categoryId}`;
    const docRef = doc(db, docPath);
    
    // Prepare items data for Firestore
    const itemsData = items.map(item => ({
      nameEn: item.nameEn,
      nameAr: item.nameAr,
      data: item.data,
      action: item.action,
      descriptionEn: item.descriptionEn || '',
      descriptionAr: item.descriptionAr || '',
      categoryId: item.categoryId,
      isActive: item.isActive,
      createdAt: item.createdAt || getCurrentMillis(),
      updatedAt: getCurrentMillis(),
      createdBy: item.createdBy || 'system',
      updatedBy: 'system'
    }));
    
    // Update the document with the items array
    await updateDoc(docRef, {
      items: itemsData,
      updatedAt: getCurrentMillis(),
      updatedBy: 'system'
    });
  } catch (error) {
    console.error('Error pushing items to category:', error);
    throw error;
  }
};

